// Enter = Tab ve formulářích pro zadávání dat (rozhodnutí zadavatele 9. 10. 2026, docs/01): Enter v poli přesune
// fokus na další pole, Shift+Enter na předchozí. Enter nikdy nic neuloží ani neodešle — uložení a odeslání jen
// tlačítkem; na posledním poli Enter přesune fokus na hlavní tlačítko, ale nestiskne ho. Jeden posluchač pro celou
// aplikaci (`useEnterJakoTab` v kořeni), takže platí ve všech dialozích, zásuvkách a formulářích bez úprav obrazovek.
//
// Výjimky (Enter dělá svou věc): vyhledávací pole (`type="search"`, `role="search"`), víceřádková
// pole (`textarea` — Enter = nový řádek; jednořádkový `RostouciRadek` s `data-radek="jeden"` výjimkou není),
// otevřený našeptávač / rozbalovací seznam (`aria-expanded="true"` — Enter
// vybírá položku, další Enter teprve posouvá), tlačítka, odkazy a výběry; a pole nebo celek označený
// `data-enter="vlastni"` (např. přihlášení, chat s asistentem, „hledat v ARES“).
import { useEffect } from 'react';

/** Typy `input`, ve kterých se píše nebo vybírá hodnota a kde Enter jinak odesílá formulář. */
const TYPY_POLI = new Set([
  '',
  'text',
  'number',
  'email',
  'tel',
  'url',
  'password',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
]);

const POLE = 'input, select, textarea';
const HLAVNI_TLACITKO =
  'button[type="submit"]:not(:disabled), button.tlacitko--primarni:not(:disabled), button.tlacitko--nebezpecne:not(:disabled)';

function jeViditelne(prvek: HTMLElement): boolean {
  if (prvek.hidden || prvek.closest('[hidden], [inert]')) return false;
  const styl = getComputedStyle(prvek);
  return styl.display !== 'none' && styl.visibility !== 'hidden';
}

function jePole(prvek: HTMLElement): boolean {
  if (prvek instanceof HTMLInputElement) {
    return prvek.type !== 'hidden' && !prvek.disabled && prvek.tabIndex >= 0 && jeViditelne(prvek);
  }
  if (prvek instanceof HTMLSelectElement || prvek instanceof HTMLTextAreaElement) {
    return !prvek.disabled && prvek.tabIndex >= 0 && jeViditelne(prvek);
  }
  return false;
}

/** Pole, na kterém Enter přesouvá fokus (jinak událost necháme být). */
function jePoleSEnterJakoTab(
  cil: EventTarget | null,
): cil is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
  // Rozbalovací výběr (`select`) je pole jako ostatní; seznam s víc řádky (multiple, size > 1) si Enter nechá.
  if (cil instanceof HTMLSelectElement) {
    return (
      !cil.multiple && cil.size <= 1 && !cil.closest('[data-enter="vlastni"], [role="search"]')
    );
  }
  // Jednořádkové pole, které jen roste s textem (`RostouciRadek`, `data-radek="jeden"`), je pole jako input.
  if (cil instanceof HTMLTextAreaElement) {
    return cil.dataset.radek === 'jeden' && !cil.closest('[data-enter="vlastni"], [role="search"]');
  }
  if (!(cil instanceof HTMLInputElement)) return false;
  if (!TYPY_POLI.has(cil.type)) return false;
  // Zavřený našeptávač se chová jako obyčejné pole; otevřený seznam si Enter bere (výběr položky).
  if (cil.getAttribute('aria-expanded') === 'true') return false;
  if (cil.getAttribute('aria-activedescendant')) return false;
  if (cil.closest('[data-enter="vlastni"], [role="search"]')) return false;
  return true;
}

/** Pole v témže dialogu, zásuvce nebo formuláři jako `pole`, v pořadí, jak jdou pod sebou. */
export function poleVOkoli(pole: HTMLElement): { obal: HTMLElement; pole: HTMLElement[] } | null {
  const obal = pole.closest<HTMLElement>('[role="dialog"], form');
  if (!obal) return null;
  return { obal, pole: Array.from(obal.querySelectorAll<HTMLElement>(POLE)).filter(jePole) };
}

/**
 * Zpracuje stisk Enter v poli. Vrací `true`, když událost patří nám (volající zavolá `preventDefault`).
 * Samostatně exportovaná kvůli testům.
 */
export function enterJakoTab(udalost: KeyboardEvent): boolean {
  if (udalost.key !== 'Enter' || udalost.isComposing) return false;
  if (udalost.ctrlKey || udalost.metaKey || udalost.altKey) return false;
  const cil = udalost.target;
  if (!jePoleSEnterJakoTab(cil)) return false;
  const okoli = poleVOkoli(cil);
  if (!okoli) return false;

  const index = okoli.pole.indexOf(cil);
  const dalsi = okoli.pole[index + (udalost.shiftKey ? -1 : 1)];
  if (dalsi) {
    dalsi.focus();
    if (dalsi instanceof HTMLInputElement && TYPY_POLI.has(dalsi.type)) {
      try {
        dalsi.select();
      } catch {
        // Typy bez výběru textu (date, number v některých prohlížečích) — fokus stačí.
      }
    }
  } else if (!udalost.shiftKey) {
    // Poslední pole: fokus na hlavní tlačítko, ale nestisknout ho.
    okoli.obal.querySelector<HTMLElement>(HLAVNI_TLACITKO)?.focus();
  }
  return true;
}

/** Tablet: virtuální klávesnice místo „Odeslat“ ukáže „Další“. */
function nastavTipKlavesy(udalost: FocusEvent) {
  const cil = udalost.target;
  if (!jePoleSEnterJakoTab(cil) || cil instanceof HTMLSelectElement) return;
  if (cil.hasAttribute('enterkeyhint')) return;
  if (cil.closest('[role="dialog"], form')) cil.setAttribute('enterkeyhint', 'next');
}

/** Připojí Enter = Tab na celou aplikaci (volat jednou v kořeni). */
export function useEnterJakoTab() {
  useEffect(() => {
    const naKlavesu = (udalost: KeyboardEvent) => {
      if (enterJakoTab(udalost)) udalost.preventDefault();
    };
    // Fáze zachytávání: dřív než formulář pošle implicitní odeslání a dřív než Enter v React obsluze pole.
    document.addEventListener('keydown', naKlavesu, true);
    document.addEventListener('focusin', nastavTipKlavesy);
    return () => {
      document.removeEventListener('keydown', naKlavesu, true);
      document.removeEventListener('focusin', nastavTipKlavesy);
    };
  }, []);
}
